import {
  DIAGNOSTIC_90792_DURATION_WARNING_TOLERANCE_MINUTES,
  DIAGNOSTIC_90792_STANDARD_MINUTES,
  EM_CODES_ESTABLISHED,
  EM_CODES_NEW,
  INTERACTIVE_COMPLEXITY_SUPPORTED_PRIMARY_CODES,
  PSYCHOTHERAPY_90832_MAX_MINUTES,
  PSYCHOTHERAPY_90832_MIN_MINUTES,
  PSYCHOTHERAPY_90834_MAX_MINUTES,
  PSYCHOTHERAPY_90834_MIN_MINUTES,
  PSYCHOTHERAPY_90837_MIN_MINUTES,
  PSYCHOTHERAPY_MIN_BILLABLE_MINUTES,
} from './constants';
import type { EncounterScrubberInput, MdmLevel, PatientStatus } from './types';

export const expectedEmCodeByPatientStatusAndMdm = {
  new: {
    straightforward: '99202',
    low: '99203',
    moderate: '99204',
    high: '99205',
  },
  established: {
    straightforward: '99212',
    low: '99213',
    moderate: '99214',
    high: '99215',
  },
} as const satisfies Record<PatientStatus, Record<MdmLevel, string>>;

export function getExpectedPsychotherapyCodeByDuration(
  durationMinutes: number | undefined,
): '90832' | '90834' | '90837' | undefined {
  if (durationMinutes == null || !Number.isFinite(durationMinutes)) return undefined;
  if (durationMinutes < PSYCHOTHERAPY_MIN_BILLABLE_MINUTES) return undefined;
  if (durationMinutes <= PSYCHOTHERAPY_90832_MAX_MINUTES) return '90832';
  if (durationMinutes <= PSYCHOTHERAPY_90834_MAX_MINUTES) return '90834';
  if (durationMinutes >= PSYCHOTHERAPY_90837_MIN_MINUTES) return '90837';
  return undefined;
}

export function getExpectedEmCode(
  patientStatus: PatientStatus | undefined,
  mdmLevel: MdmLevel | undefined,
): string | undefined {
  if (!patientStatus || !mdmLevel) return undefined;
  return expectedEmCodeByPatientStatusAndMdm[patientStatus][mdmLevel];
}

export function calculate90840Units(durationMinutes: number | undefined): number {
  if (durationMinutes == null || !Number.isFinite(durationMinutes)) return 0;
  if (durationMinutes < 75) return 0;
  if (durationMinutes <= 90) return 1;
  return 1 + Math.ceil((durationMinutes - 90) / 30);
}

export function isInteractiveComplexitySupportedPrimaryPresent(currentCptCodes: string[]): boolean {
  return currentCptCodes.some((code) =>
    (INTERACTIVE_COMPLEXITY_SUPPORTED_PRIMARY_CODES as readonly string[]).includes(
      code.trim().toUpperCase(),
    ),
  );
}

export function is96127Eligible(
  input: Pick<EncounterScrubberInput, 'payerCodesList' | 'assessmentPerformedSameDateAsSession'>,
): boolean {
  return (
    input.payerCodesList.includes('96127') && input.assessmentPerformedSameDateAsSession === true
  );
}

export function durationMateriallyDiffersFrom90792Standard(durationMinutes: number | undefined): boolean {
  if (durationMinutes == null || !Number.isFinite(durationMinutes)) return false;
  return (
    Math.abs(durationMinutes - DIAGNOSTIC_90792_STANDARD_MINUTES) >
    DIAGNOSTIC_90792_DURATION_WARNING_TOLERANCE_MINUTES
  );
}

export function emCodeRank(code: string, patientStatus: PatientStatus): number {
  const order = patientStatus === 'new' ? EM_CODES_NEW : EM_CODES_ESTABLISHED;
  const idx = (order as readonly string[]).indexOf(code);
  return idx >= 0 ? idx : -1;
}

export function psychotherapyDurationMessage(
  code: string,
  durationMinutes: number | undefined,
  suggested: string,
): string {
  const durationLabel =
    durationMinutes != null ? `${durationMinutes} minutes` : 'unknown duration';
  return `${code} was selected, but duration is ${durationLabel}. Suggest ${suggested}.`;
}

export function psychotherapyKeepMessage(code: string, durationMinutes: number | undefined): string {
  const durationLabel =
    durationMinutes != null ? `${durationMinutes} minutes` : 'documented duration';
  return `${code} matches ${durationLabel}.`;
}

/** Duration band labels for psychotherapy codes. */
export function psychotherapyBandForCode(code: string): string {
  switch (code) {
    case '90832':
      return `${PSYCHOTHERAPY_90832_MIN_MINUTES}–${PSYCHOTHERAPY_90832_MAX_MINUTES} min`;
    case '90834':
      return `${PSYCHOTHERAPY_90834_MIN_MINUTES}–${PSYCHOTHERAPY_90834_MAX_MINUTES} min`;
    case '90837':
      return `${PSYCHOTHERAPY_90837_MIN_MINUTES}+ min`;
    default:
      return '';
  }
}
